import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { config } from '@platform/constants';
import { httpService } from '@shared/utilities';
import { AscResponse } from '@shared/models';
import * as Response from '@shared/models/rewards/rewards.response.type';
import * as Request from '@shared/models/rewards/rewards.request.type';
import * as ResourceResponse from '@shared/models/resources/resource.response.type';
import * as ResourceRequest from '@shared/models/resources/resource.request.type';
import { formatErrorMessage } from '@ascendnow/ascend-components';
import { setRewardDashboardSections } from '../slices/reward.slice';
import { setRewardRecordings } from '../slices/recording.slice';
import { setRewardSingleRecording } from '../slices/single-recording.slice';
import { setPurchasedRewards } from '../slices/purchased.slice';
import { setRewardSkills } from '../slices/subject.slice';

function defineRecordingType(type: Request.RecordingType) {
    let currentType = type.toLowerCase();
    if (currentType === 'webinar' || currentType === 'workshop') {
        currentType = currentType + 's';
    } else if (
        currentType === 'guide' ||
        currentType === 'notes' ||
        currentType === 'slides' ||
        currentType === 'infographics'
    ) {
        currentType = 'resources';
    }

    return currentType;
}

export const RewardEndpoints = {
    getRewardDashboardRecordings: config.base_url + '/rewards/dashboard',
    getRewardRecordings: config.base_url + '/rewards/recordings',
    getRewardSingleRecording: config.base_url + '/rewards/purchases/',
    getRewardsPurchased: config.base_url + '/rewards/reward-purchases',
    unlockReward: config.base_url + '/rewards/purchase',
    unlockRecording: config.base_url + '/rewards/recordings',
    sendMessageToMentor: config.base_url + '/rewards/reward-purchases/requests',
    requestMentoringSession: config.base_url + '/rewards/reward-purchases/:purchaseId',
    getSkillCategories: config.base_url + '/resources',
};

export const fetchRewardDashboardSections = createAsyncThunk<void, Request.IRewardDashboardRecordingsQueryType>(
    RewardEndpoints.getRewardDashboardRecordings,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IRewardDashboard>>(
                `${RewardEndpoints.getRewardDashboardRecordings}`
            );

            dispatch(setRewardDashboardSections(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching data from query ${RewardEndpoints.getRewardDashboardRecordings}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchRewardRecordings = createAsyncThunk<void, Request.IRewardRecordingsQueryType>(
    RewardEndpoints.getRewardRecordings,
    async (query, { dispatch }) => {
        try {
            const params = query.params ? new URLSearchParams({ ...query.params }) : '';
            const res = await httpService.get<AscResponse<Response.IRecording[]>>(
                `${RewardEndpoints.getRewardRecordings}?${params}`
            );

            dispatch(setRewardRecordings(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(err, `Error fetching data from query ${RewardEndpoints.getRewardRecordings}`),
                {
                    type: 'error',
                }
            );
        }
    }
);

export const fetchRewardSingleRecording = createAsyncThunk<void, Request.IRewardSingleRecordingQueryType>(
    RewardEndpoints.getRewardSingleRecording,
    async (query, { dispatch }) => {
        try {
            const { recordingId, type } = query.params;

            const res = await httpService.get<AscResponse<Response.ISingleRecording>>(
                `${RewardEndpoints.getRewardSingleRecording}${recordingId}?type=${defineRecordingType(type)}`
            );

            dispatch(setRewardSingleRecording(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(err, `Error fetching data from query ${RewardEndpoints.getRewardSingleRecording}`),
                { type: 'error' }
            );
        }
    }
);

export const fetchRewardsPurchased = createAsyncThunk<void, Request.IRewardsPurchasedQueryType>(
    RewardEndpoints.getRewardsPurchased,
    async (query, { dispatch }) => {
        try {
            const params = query.params ? new URLSearchParams({ ...query.params }) : '';
            const res = await httpService.get<AscResponse<Response.IRewardPurchase[]>>(
                `${RewardEndpoints.getRewardsPurchased}?${params}`
            );

            dispatch(setPurchasedRewards(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(err, `Error fetching data from query ${RewardEndpoints.getRewardsPurchased}`),
                {
                    type: 'error',
                }
            );
        }
    }
);

export const unlockReward = createAsyncThunk<void, Request.IRewardUnlockRewardQueryType>(
    RewardEndpoints.unlockReward,
    (query, { dispatch }) => {
        return httpService
            .get<AscResponse<{ isUnlocked: boolean }>>(`${RewardEndpoints.unlockReward}/${query.params.rewardId}`)
            .then((res) => {
                if (query.cb && query.cb.onSuccess) {
                    query.cb.onSuccess(res.data.data);
                }
            })
            .catch((err) => {
                if (query.cb && query.cb.onError) {
                    query.cb.onError(err);
                }
                console.log(formatErrorMessage(err, `Error fetching data from query ${RewardEndpoints.unlockReward}`), {
                    type: 'error',
                });
            });
    }
);

export const unlockRecording = createAsyncThunk<void, Request.IRewardUnlockRecordingQueryType>(
    RewardEndpoints.unlockRecording,
    (query, { dispatch }) => {
        return httpService
            .get<AscResponse<{ isUnlocked: boolean }>>(
                `${RewardEndpoints.unlockRecording}/${query.params.recordingId}?type=${defineRecordingType(
                    query.params.type
                )}`
            )
            .then((res) => {
                if (query.cb && query.cb.onSuccess) {
                    query.cb.onSuccess(res.data.data);
                }
            })
            .catch((err) => {
                if (query.cb && query.cb.onError) {
                    query.cb.onError(err);
                }
                toast.error(
                    formatErrorMessage(err, `Error fetching data from query ${RewardEndpoints.unlockRecording}`)
                );
                console.log(
                    formatErrorMessage(err, `Error fetching data from query ${RewardEndpoints.unlockRecording}`),
                    {
                        type: 'error',
                    }
                );
            });
    }
);

export const sendMessageToMentor = createAsyncThunk<void, Request.IRewardSendMessageQueryType>(
    RewardEndpoints.sendMessageToMentor,
    (query, { dispatch }) => {
        return httpService
            .post<AscResponse<Response.IMessageResponse>>(
                `${RewardEndpoints.sendMessageToMentor}/${query.params.requestId}/message`,
                query.params.body
            )
            .then((res) => {
                if (query.cb && query.cb.onSuccess) {
                    query.cb.onSuccess(res.data.data);
                }
            })
            .catch((err) => {
                if (query.cb && query.cb.onError) {
                    query.cb.onError(err);
                }
                console.log(
                    formatErrorMessage(err, `Error fetching data from query ${RewardEndpoints.sendMessageToMentor}`),
                    {
                        type: 'error',
                    }
                );
            });
    }
);

export const sendRequestForMentoringSession = createAsyncThunk<void, Request.IRewardMentoringSessionQueryType>(
    RewardEndpoints.requestMentoringSession,
    (query, { dispatch }) => {
        return httpService
            .post<AscResponse<Response.IMessageResponse>>(
                `${RewardEndpoints.getRewardsPurchased}/${query.params.purchaseId}`,
                query.params.body
            )
            .then((res) => {
                if (query.cb && query.cb.onSuccess) {
                    query.cb.onSuccess(res.data.data);
                }
            })
            .catch((err) => {
                if (query.cb && query.cb.onError) {
                    query.cb.onError(err);
                }
                console.log(
                    formatErrorMessage(
                        err,
                        `Error fetching data from query ${RewardEndpoints.requestMentoringSession}`
                    ),
                    {
                        type: 'error',
                    }
                );
            });
    }
);

export const fetchRewardSkillCategories = createAsyncThunk<void, ResourceRequest.IResourceCategoryQueryType>(
    RewardEndpoints.getSkillCategories,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<ResourceResponse.ResourceCategoryResponseType>>(
                `${RewardEndpoints.getSkillCategories}`
            );

            dispatch(setRewardSkills(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching reward skills from query ${RewardEndpoints.getSkillCategories}`
                ),
                {
                    type: 'error',
                }
            );
        }
    }
);
